import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql';
import { modifyAutoApplyInfo } from '../../../lib/apollo/client';
import { useSqPrefillPreference } from '../hooks/useSqPrefillPreference';
import {
    Box,
    Flex,
    Text,
    Button,
    ButtonLink,
    Spinner,
    Alert,
    Switch,
    Heading,
    TextLink,
} from '@indeed/ifl-components';
import {
    Icon,
    FileDoc,
    CheckCircle,
    Edit,
    Upload,
    Information,
    NewWindow,
    Resume,
} from '@indeed/ifl-icons';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { AutoApplyHeader } from '../../../components/AutoApplyHeader/AutoApplyHeader';
import { ErrorDialog } from '../../../components/ErrorDialog/ErrorDialog';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import { FileResumePreview } from '../components/FileResumePreview';
import GettextFactory from '../../../util/GettextFactory';
import { jsProThemeButton } from '../components/jsProTheme/jsProTheme';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import { useIsProBrandingEnabled } from '../../../lib/hooks/useIsProBrandingEnabled';
import { useApplyForMeCopy } from '../hooks/useApplyForMeCopy';
import {
    useGetAutoApplyResumesQuery,
    useDownloadFileResumeMutation,
    useCreateUploadResumeUrlMutation,
    useAddJobSeekerProfileResumeFileMutation,
    useDeleteResumeBundleMutation,
    useUpdateSelectedResumeTypeMutation,
} from './__generated__/ResumePage.generated';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplySetupButtonClickEvent';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { mapLatestApplyResumeType } from '../resumePrefill';
import { markProfileModified } from '../AutoApplySetupSessionState';
import { StickyActionFooter } from '../../../components/StickyActionFooter/StickyActionFooter';

type ResumeType = 'indeed' | 'file';

interface SelectedResume {
    type: ResumeType;
    id: string;
}

/**
 * PFSV errors where the upload itself succeeded but post-upload processing
 * failed (text extraction or format conversion). The file is still usable, so
 * callers should treat these as non-blocking and skip the error dialog.
 *
 * Mirrors `NON_BLOCKING_ERROR_CODES` in
 * `smart-apply-bff/src/main/java/com/indeed/smartapply/bff/service/PfsvErrorUtils.java`.
 *
 * PFSV surfaces the code inside the error `message` (e.g.
 * `"PFSV_E-102: Resume parsing error"`) — not in `extensions.code`, which only
 * carries the generic Apollo error type (`INTERNAL`, …).
 */
const NON_BLOCKING_PFSV_ERROR_CODES: ReadonlySet<string> = new Set([
    'PFSV_E-050', // DOCUMENT_CONVERSION_ERROR
    'PFSV_E-102', // RESUME_PARSING_ERROR
]);

const PFSV_ERROR_CODE_PATTERN = /PFSV_E-\d{2,3}/;

export const isNonBlockingPfsvError = (error: GraphQLFormattedError): boolean => {
    const code = error.message.match(PFSV_ERROR_CODE_PATTERN)?.[0];
    return code !== undefined && NON_BLOCKING_PFSV_ERROR_CODES.has(code);
};

/**
 * Mapping of Profile File Service error codes to user-friendly translated messages.
 * Error codes come from PFSV_E-XXX format (e.g., PFSV_E-023).
 * @see https://link.indeed.tech/GLYNHD82F
 */
const getFileUploadErrorMessage = (
    errorMessage: string,
    gt: ReturnType<typeof GettextFactory>,
): string => {
    // Extract error code from messages like "PFSV_E-023: The file you selected is too small"
    const codeMatch = errorMessage.match(/PFSV_E-0?(\d+)/);
    const errorCode = codeMatch ? codeMatch[1] : null;

    const errorMessages: Record<string, string> = {
        '21': gt.gettext('The file you selected is empty. Please upload a valid resume file.'),
        '22': gt.gettext('The file you selected is too large. Please upload a smaller file.'),
        '23': gt.gettext(
            'The file you selected is too small. Please upload a valid resume file with more content.',
        ),
        '24': gt.gettext(
            'That file type is not supported. Please upload a PDF, DOCX, RTF, or TXT file.',
        ),
        '25': gt.gettext(
            'You have reached the maximum number of uploaded resumes. Please delete an existing resume before uploading a new one.',
        ),
        '26': gt.gettext(
            'We could not read your file. Please try uploading a different file or format.',
        ),
        '27': gt.gettext('The file name is empty. Please rename your file and try again.'),
        '28': gt.gettext('The file name is too long. Please use a shorter file name.'),
        '29': gt.gettext(
            "The file content doesn't match the file type. Please ensure your file is a valid resume document.",
        ),
        '30': gt.gettext(
            'This file could not be uploaded for security reasons. Please try a different file.',
        ),
    };

    if (errorCode && errorMessages[errorCode]) {
        return errorMessages[errorCode];
    }

    // Fallback to default error message
    return gt.gettext('There was a problem uploading your file. Please try again.');
};

/**
 * Resume Page for Auto Apply Setup
 * Shows resume selection options and review after selection
 *
 * Figma Designs:
 * - Review view: https://www.figma.com/design/pXXAQfQFDZqsF1BxMo5ccB/-CS--Assisted-apply-2.0--Auto-Apply-?node-id=6750-85507&t=h3Y8lmCBkGF6iJ0d-0
 */
const ResumePage: React.FC = () => {
    const { goForward, exitSubFlow, loading: pageFlowLoading } = usePageFlow();
    const apolloClient = useApolloClient();
    const gt = GettextFactory();
    const location = useLocation();
    const { locale, countryCode, ipCountryCode, indeedProfileBaseUrl, origin, ctk } =
        useInitialState();
    const eventLogger = useEventLogger();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isProBrandingEnabled = useIsProBrandingEnabled();
    const copy = useApplyForMeCopy();

    const [selectedResume, setSelectedResume] = useState<SelectedResume | null>(null);
    const [uploading, setUploading] = useState(false);
    const switchId = React.useId();

    // Edit mode is determined by the route path, not query params
    const isEditMode = location.pathname.endsWith('/edit');
    // Cache PDF URLs by file ID to avoid redundant fetches (stores null for failed fetches)
    const [pdfUrlCache, setPdfUrlCache] = useState<Record<string, string | null>>({});
    // Error state for upload failures and query failures
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    // Custom error message for user-friendly display
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    const { getGroup } = useProctorGroups();
    const sqPrefillEnabled = getGroup('js_pro_auto_apply_sq_prefill_tst')?.value === 1;
    // Progressive-automation banner entry: pre-fill the resume selection from
    // the user's latest apply when they have no explicit setup choice yet.
    const isProgressiveAutomation = getGroup('rnhp_auto_apply_banner_tst')?.value === 2;
    // The SQ prefill ("Use AI to answer application questions") section is hidden
    // under progressive automation, so treat prefill as off for both the section
    // render and its preference persistence even when the prefill test is on.
    const showSqPrefillSection = sqPrefillEnabled && !isProgressiveAutomation;

    // Build Indeed Resume edit URL with proper query parameters
    // Uses useLocation (SSR-safe) and origin from server (includes protocol)
    const indeedResumeEditLinkUrl = useMemo(() => {
        const url = new URL('/resume', indeedProfileBaseUrl);
        url.searchParams.set('co', countryCode);
        url.searchParams.set('hl', locale);
        url.searchParams.set('from', 'auto-apply-preference');

        // Build continue URL using location (works in SSR) and origin from server
        const continueUrl = new URL(`${origin}${location.pathname}${location.search}`);
        continueUrl.searchParams.set('from', 'profile-resume-edit');
        url.searchParams.set('continue', continueUrl.toString());

        return url.toString();
    }, [indeedProfileBaseUrl, countryCode, locale, origin, location.pathname, location.search]);

    // Fetch resume data from GraphQL
    const {
        data,
        refetch,
        error: queryError,
    } = useGetAutoApplyResumesQuery({
        errorPolicy: 'all',
        fetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
    });

    // Mutation to fetch PDF URL
    const [downloadFileResume] = useDownloadFileResumeMutation();

    // Upload mutations
    const [createUploadUrl] = useCreateUploadResumeUrlMutation();
    const [addResumeFile] = useAddJobSeekerProfileResumeFileMutation();
    // Dual-resume V2 cleanup (PROFPLAT-9559): the Resume Collection only
    // supports one file per accountId in M0, so any existing RESUME-type
    // files must be deleted before persisting a newly uploaded one.
    const [deleteResumeBundle] = useDeleteResumeBundleMutation();

    // Mutation to update selected resume type
    const [updateSelectedResumeType, { loading: updateLoading }] =
        useUpdateSelectedResumeTypeMutation();

    // Dual-resume migration (PROF-9839): when enabled, read uploaded file
    // resumes from the new `jobSeekerResumes` platform; otherwise keep the
    // legacy `fileInformation` source. Indeed Resume is always read from the
    // legacy `jobSeekerProfile.profile.resume` path — the new platform does
    // not return Indeed Resumes during M0.
    const isDualResumesEnabled = data?.jobSeekerProfileDualResumesEnabled?.enabled === true;

    // Extract resume data
    const indeedResume = useMemo(() => {
        return data?.jobSeekerProfile?.profile?.resume.find(
            (resume) => resume.resumeType === 'INDEED_RESUME',
        );
    }, [data?.jobSeekerProfile?.profile?.resume]);

    // V2 file resumes derived from `jobSeekerResumes`, mapped to the same
    // shape as legacy so downstream rendering stays identical. Each bundle's
    // `pdfResumeFile.url` is captured to prime the PDF preview cache without
    // an extra round trip on first paint.
    const v2FileResumes = useMemo(() => {
        const bundles = data?.jobSeekerResumes?.resumes ?? [];
        return bundles
            .map((bundle) => {
                const metadata = bundle.originalUploadedFile?.metadata;
                if (!metadata || metadata.fileType !== 'RESUME') return null;
                return {
                    resumeId: bundle.resumeId,
                    id: metadata.id,
                    fileName: metadata.fileName ?? null,
                    fileType: metadata.fileType ?? null,
                    uploadTimestamp: metadata.uploadTimestamp ?? null,
                    pdfUrl: bundle.pdfResumeFile?.url ?? null,
                };
            })
            .filter((file): file is NonNullable<typeof file> => file !== null);
    }, [data?.jobSeekerResumes?.resumes]);

    const legacyFileResumes = useMemo(() => {
        return (
            data?.jobSeekerProfile?.profile?.fileInformation?.filter(
                (file) => file.fileType === 'RESUME',
            ) || []
        );
    }, [data?.jobSeekerProfile?.profile?.fileInformation]);

    const fileResumes = useMemo(() => {
        return isDualResumesEnabled ? v2FileResumes : legacyFileResumes;
    }, [isDualResumesEnabled, v2FileResumes, legacyFileResumes]);

    // Resume type to pre-select: the user's saved setup choice, falling back to
    // their latest-apply resume type under the progressive-automation bucket
    // (IAFE-2079). See mapLatestApplyResumeType for the latest-apply → setup
    // mapping (e.g. TAILORED_RESUME → Indeed Resume).
    const selectedResumeType =
        data?.autoApplyInfo?.selectedResumeType ??
        (isProgressiveAutomation
            ? mapLatestApplyResumeType(data?.jobSeekerProfile?.profile?.latestApply?.resumeType)
            : undefined);

    const {
        usePastAnswers,
        handleUsePastAnswersChange,
        error: sqPrefillMutationError,
    } = useSqPrefillPreference(data?.autoApplyInfo?.sqPastAnswerPrefillPreference);

    // Show error dialog on query or mutation failure
    // Ignore query error if autoApplyInfo fails but jobSeekerProfile succeeds
    useEffect(() => {
        if ((queryError && !data?.jobSeekerProfile) || sqPrefillMutationError) {
            setShowErrorDialog(true);
        }
    }, [queryError, data?.jobSeekerProfile, sqPrefillMutationError]);

    // Check if user has no resumes at all (empty state)
    const hasNoResumes = !indeedResume && fileResumes.length === 0;

    // Pre-select resume based on selectedResumeType from GraphQL response or query parameter
    useEffect(() => {
        // Only pre-select if we have data and no resume is currently selected
        if (!data || selectedResume) return;

        // Check if user is coming back from Indeed Resume edit page
        const searchParams = new URLSearchParams(location.search);
        const fromParam = searchParams.get('from');

        if (fromParam === 'profile-resume-edit' && indeedResume) {
            // Pre-select Indeed Resume when returning from edit page
            setSelectedResume({ type: 'indeed', id: indeedResume.id });
        } else if (selectedResumeType === 'INDEED_RESUME' && indeedResume) {
            setSelectedResume({ type: 'indeed', id: indeedResume.id });
        } else if (selectedResumeType === 'SAVED_FILE_RESUME' && fileResumes.length > 0) {
            // Pre-select the most recently uploaded file resume
            const sortedFileResumes = [...fileResumes].sort((a, b) => {
                const timeA = Number(a.uploadTimestamp) || 0;
                const timeB = Number(b.uploadTimestamp) || 0;
                return timeB - timeA;
            });
            if (sortedFileResumes[0]) {
                setSelectedResume({ type: 'file', id: sortedFileResumes[0].id });
            }
        }
    }, [data, selectedResumeType, indeedResume, fileResumes, selectedResume, location.search]);

    // Format date
    const formatDate = (timestamp: string | number | null | undefined) => {
        if (!timestamp) return '';
        return new Intl.DateTimeFormat(locale, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }).format(new Date(timestamp));
    };

    // Prime the PDF cache from the V2 query response (URL is returned inline).
    useEffect(() => {
        if (!isDualResumesEnabled) return;
        if (v2FileResumes.length === 0) return;
        setPdfUrlCache((prev) => {
            let next = prev;
            for (const file of v2FileResumes) {
                if (next[file.id] === file.pdfUrl) continue;
                if (next === prev) next = { ...prev };
                next[file.id] = file.pdfUrl;
            }
            return next;
        });
    }, [isDualResumesEnabled, v2FileResumes]);

    // Fetch PDF URL when a file resume is selected (legacy path only).
    useEffect(() => {
        if (isDualResumesEnabled) return;
        const fetchPdfUrl = async () => {
            if (selectedResume?.type === 'file' && selectedResume?.id) {
                // Check cache first (including null values)
                if (selectedResume.id in pdfUrlCache) {
                    return; // Already cached
                }

                // Fetch from server if not in cache
                try {
                    const result = await downloadFileResume({
                        variables: { fileId: selectedResume.id },
                    });
                    const url =
                        result.data?.createJobSeekerProfileResumeFileTempUrls?.pdfResumeUrl || null;
                    setPdfUrlCache((prev) => ({
                        ...prev,
                        [selectedResume.id]: url,
                    }));
                } catch (error) {
                    console.error('Failed to fetch PDF URL:', error);
                    setPdfUrlCache((prev) => ({
                        ...prev,
                        [selectedResume.id]: null,
                    }));
                }
            }
        };

        void fetchPdfUrl();
    }, [isDualResumesEnabled, selectedResume, downloadFileResume, pdfUrlCache]);

    const handleContinue = async () => {
        eventLogger.logAutoApplySetupButtonClickEvent?.({
            currentPage: location.pathname,
            button: 'continue',
        });
        if (!selectedResume) return;

        // Map internal resume type to GraphQL enum
        const resumeType = selectedResume.type === 'indeed' ? 'INDEED_RESUME' : 'SAVED_FILE_RESUME';

        try {
            // Save the selected resume type
            const result = await updateSelectedResumeType({
                variables: {
                    input: {
                        selectedResumeType: resumeType,
                        trackApplicantInfo: true,
                        locale,
                        country: countryCode,
                        ctk,
                    },
                },
            });

            // Verify the response matches JS intent (ITV check)
            const responseResumeType = result.data?.updateAutoApply?.selectedResumeType;
            if (responseResumeType !== resumeType) {
                setShowErrorDialog(true);
                return;
            }

            // Only update cache after (ITV) verification passes
            // This ensures the review page can use the verified cached value
            if (result.data?.updateAutoApply?.selectedResumeType) {
                markProfileModified();
                modifyAutoApplyInfo(apolloClient.cache, {
                    selectedResumeType() {
                        return result.data!.updateAutoApply.selectedResumeType;
                    },
                });
            }

            // Persist SQ prefill preference for new users (server value is null)
            // The frontend defaults null to OPT_IN, but the backend defaults to OPT_OUT,
            // so we must explicitly save the user's visible preference on Continue.
            if (
                showSqPrefillSection &&
                data?.autoApplyInfo?.sqPastAnswerPrefillPreference == null
            ) {
                void handleUsePastAnswersChange(usePastAnswers);
            }

            // In edit mode the resume edit page is the final sub-flow step, so
            // exit the detour explicitly back to the origin (Review). In normal
            // mode advance to the next main-flow step (IAFE-383).
            if (isEditMode) {
                // Return to the specific Review variant the user came from, carried
                // in the `reviewVariant` search param (IAFE-2551). This fallback is
                // only used when the recorded subFlowOrigin is gone (e.g. after the
                // external profile-resume-editor round-trip, which preserves the
                // param via the `continue` URL); a normal in-app exit still pops
                // straight back to the origin entry. Default to the base Review route
                // when no valid variant is present (non-progressive flow / cold entry).
                const reviewVariant = new URLSearchParams(location.search).get('reviewVariant');
                const isKnownVariant =
                    reviewVariant === 'review-before-submit' || reviewVariant === 'apply-for-me';
                await exitSubFlow(
                    isKnownVariant
                        ? `/auto-apply-setup/review/${reviewVariant}`
                        : '/auto-apply-setup/review',
                );
            } else {
                await goForward();
            }
        } catch {
            setShowErrorDialog(true);
        }
    };

    const handleRadioChange = (type: ResumeType, id: string) => {
        setSelectedResume({ type, id });
    };

    // Generate unique radio value
    const getRadioValue = (type: ResumeType, id: string) => `${type}-${id}`;
    const selectedRadioValue = selectedResume
        ? getRadioValue(selectedResume.type, selectedResume.id)
        : '';

    // Get MIME type from file extension
    const getMimeType = (fileName: string): string => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        const mimeTypes: Record<string, string> = {
            pdf: 'application/pdf',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            txt: 'text/plain',
            rtf: 'application/rtf',
        };
        return mimeTypes[extension || ''] || 'application/octet-stream';
    };

    // Handle file upload
    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        // Defensive check for iOS WKWebView edge cases where event might be undefined
        // This can happen when the file picker is cancelled on some iOS versions
        if (!event?.target?.files) {
            return;
        }
        const file = event.target.files[0];
        if (!file) return;

        setUploading(true);
        setShowErrorDialog(false);

        let didDeleteResume = false;

        try {
            // Step 1: Get temporary upload URL and fileId
            const urlResult = await createUploadUrl();
            const uploadURL =
                urlResult.data?.createJobSeekerProfileUploadResumeFileTempUrl?.uploadURL;
            const fileId = urlResult.data?.createJobSeekerProfileUploadResumeFileTempUrl?.fileId;

            if (!uploadURL || !fileId) {
                throw new Error('Failed to get upload URL');
            }

            // Step 2: Upload file to S3
            const mimeType = getMimeType(file.name);

            const uploadResponse = await fetch(uploadURL, {
                method: 'PUT',
                headers: {
                    'Content-Type': mimeType,
                },
                body: file,
            });

            if (!uploadResponse.ok) {
                throw new Error(`Upload failed: ${uploadResponse.statusText}`);
            }

            // Step 2.5: On the dual-resume V2 path (PROFPLAT-9559), the
            // Resume Collection only supports one file per accountId in M0,
            // so delete any existing RESUME-type bundle(s) before adding
            // the newly uploaded one. The legacy path supports multiple
            // files and does not need this cleanup.
            //
            // In M0, only one resume bundle can exist; future milestones
            // will let the user pick which bundle to replace by id. Until
            // then, we delete every existing bundle so the add mutation
            // can succeed.
            //
            // The deletes are run in parallel; if any fails we surface the
            // error (the caller's catch will trigger the error dialog) so
            // the user can retry rather than ending up in a half-deleted
            // state where the subsequent add would also fail.
            if (isDualResumesEnabled && v2FileResumes.length > 0) {
                await Promise.all(
                    v2FileResumes.map(({ resumeId }) =>
                        deleteResumeBundle({
                            variables: { input: { resumeId } },
                        }),
                    ),
                );
                didDeleteResume = true;
            }

            // Step 3: Persist the uploaded file with geolocation data
            // Use errorPolicy: 'all' to allow partial data with GraphQL errors in the result,
            // so we can filter non-blocking PFSV errors (parsing / document conversion).
            // Note: Network errors will still throw.
            const addFileResult = await addResumeFile({
                variables: {
                    input: {
                        fileId,
                        geoCountry: countryCode,
                        ipCountry: ipCountryCode,
                        mediaType: mimeType,
                        name: file.name,
                        tags: [],
                    },
                },
                errorPolicy: 'all',
            });

            // Skip non-blocking PFSV errors (parsing / document conversion) — the
            // file was uploaded successfully, only post-processing failed.
            const blockingErrors = addFileResult.errors?.filter(
                (error) => !isNonBlockingPfsvError(error),
            );

            if (blockingErrors?.length) {
                throw new Error(blockingErrors[0].message);
            }

            // Refetch to get the latest resume list
            await refetch();

            // Auto-select the newly uploaded resume
            const newFileId =
                addFileResult.data?.addJobSeekerProfileResumeFile?.originalResumeFile?.id;
            if (newFileId) {
                setSelectedResume({ type: 'file', id: newFileId });
            }
        } catch (error) {
            console.error('Upload error:', error);
            if (error instanceof Error) {
                eventLogger.logAutoApplySetupErrorEvent?.({
                    currentPage: location.pathname,
                    errorMessage: AvroOptional.string(error.message),
                    errorType: AvroOptional.string('upload_error'),
                    errorStack: AvroOptional.string(error.stack),
                });
            } else {
                eventLogger.logAutoApplySetupErrorEvent?.({
                    currentPage: location.pathname,
                    errorMessage: AvroOptional.string('unknown'),
                    errorType: AvroOptional.string('upload_error'),
                });
            }
            // If we already deleted the existing resume(s) before the
            // failure (e.g. add mutation or S3 step failed after a
            // successful delete), refetch so the UI no longer shows the
            // file that has been removed server-side. Otherwise the user
            // would see a stale resume row that no longer exists.
            if (didDeleteResume) {
                try {
                    await refetch();
                } catch (refetchError) {
                    console.error('Refetch after partial-failure error:', refetchError);
                }
            }
            const rawMessage = error instanceof Error ? error.message : undefined;
            setErrorMessage(rawMessage ? getFileUploadErrorMessage(rawMessage, gt) : undefined);
            setShowErrorDialog(true);
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Trigger file input click
    const handleUploadClick = () => {
        eventLogger.logAutoApplySetupButtonClickEvent?.({
            currentPage: location.pathname,
            button: 'upload-file-resume',
        });
        fileInputRef.current?.click();
    };

    const helmet = (
        <Helmet>
            <title>
                {sqPrefillEnabled ? copy.resumeChooseFillTitle : copy.resumeAddResumeTitle}
            </title>
            <meta
                name="description"
                content={gt.gettext('Choose a resume for your automatic applications')}
            />
        </Helmet>
    );

    const retryAction = () => {
        if (queryError) {
            void refetch();
        } else if (sqPrefillMutationError) {
            void handleUsePastAnswersChange(!usePastAnswers);
        } else {
            fileInputRef.current?.click();
        }
    };

    const errorDialog = (
        <ErrorDialog
            isOpen={showErrorDialog}
            onOpenChange={setShowErrorDialog}
            onAction={retryAction}
            errorMessage={errorMessage}
        />
    );

    if (!data) {
        return (
            <PageTransition pageKey="auto-apply-resume">
                {helmet}
                <Box sx={{ padding: 4, textAlign: 'center' }}>
                    <Spinner title={gt.gettext('Loading')} />
                </Box>
                {errorDialog}
            </PageTransition>
        );
    }

    const continueButton = (
        <Button
            onClick={handleContinue}
            disabled={!selectedResume || pageFlowLoading}
            sx={isProBrandingEnabled ? jsProThemeButton.primary : {}}
            full
            loading={updateLoading || pageFlowLoading}
        >
            {isEditMode ? gt.gettext('Update') : gt.gettext('Continue')}
        </Button>
    );

    return (
        <>
            <PageTransition pageKey="auto-apply-resume">
                {helmet}

                {/* Hidden file input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.txt,.rtf"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                    aria-label={gt.gettext('Upload resume file')}
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        gap: 5,
                    }}
                >
                    <AutoApplyHeader
                        title={
                            sqPrefillEnabled
                                ? gt.gettext('Choose how we fill applications')
                                : gt.gettext('Add a resume')
                        }
                        description={
                            isProgressiveAutomation
                                ? gt.gettext("We'll save any changes to your profile.")
                                : sqPrefillEnabled
                                  ? gt.gettext("We'll use your profile to autofill applications.")
                                  : gt.gettext(
                                        "We'll use the resume from your profile in applications.",
                                    )
                        }
                    />

                    {/* Resume Section */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        {sqPrefillEnabled && (
                            <Flex sx={{ alignItems: 'center', gap: 2 }}>
                                <Box sx={{ flexShrink: 0 }}>
                                    <Resume aria-hidden />
                                </Box>
                                <Heading level={3} as="h3" sx={{ marginBlockEnd: 0 }}>
                                    {gt.gettext('Resume')}
                                </Heading>
                            </Flex>
                        )}
                        {/* Empty State - shown when no resumes exist */}
                        {hasNoResumes && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                <Alert
                                    status="warning"
                                    layout="inline"
                                    icon={<Information aria-hidden />}
                                >
                                    {copy.resumeRequiredAlert}
                                </Alert>
                                <ButtonLink
                                    variant="secondary"
                                    full
                                    href={indeedResumeEditLinkUrl}
                                    onClick={() =>
                                        eventLogger.logAutoApplySetupButtonClickEvent?.({
                                            currentPage: location.pathname,
                                            button: 'create-indeed-resume',
                                        })
                                    }
                                >
                                    {gt.gettext('Create an Indeed Resume')}
                                </ButtonLink>
                            </Box>
                        )}

                        {/* Indeed Resume Option */}
                        {indeedResume && (
                            <Box
                                as="label"
                                htmlFor={`resume-indeed-${indeedResume.id}`}
                                sx={{
                                    position: 'relative',
                                    borderRadius: 'md',
                                    padding: 4,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    gap: 2,
                                    flexDirection: 'column',
                                    transition: (theme) =>
                                        `border-color ${theme.transitions.default}`,
                                    border: '1px solid',
                                    borderColor: 'border.weak',
                                    '&:hover': {
                                        borderColor: 'border.primary',
                                    },
                                    '&:has(input:checked)': {
                                        border: '2px solid',
                                        borderColor: 'border.primary',
                                    },
                                    '&:has(input:focus-visible)': {
                                        outline: '2px solid',
                                        outlineOffset: '2px',
                                    },
                                }}
                            >
                                {/* Hidden Radio Input */}
                                <input
                                    type="radio"
                                    id={`resume-indeed-${indeedResume.id}`}
                                    name="resume-selection"
                                    value={getRadioValue('indeed', indeedResume.id)}
                                    checked={
                                        selectedRadioValue ===
                                        getRadioValue('indeed', indeedResume.id)
                                    }
                                    onChange={() => handleRadioChange('indeed', indeedResume.id)}
                                    style={{
                                        position: 'absolute',
                                        clip: 'rect(0, 0, 0, 0)',
                                        clipPath: 'inset(50%)',
                                        height: '1px',
                                        width: '1px',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                    }}
                                />

                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            alignItems: 'flex-start',
                                            flex: 1,
                                        }}
                                    >
                                        {!sqPrefillEnabled && (
                                            <Box sx={{ flexShrink: 0 }}>
                                                <Icon
                                                    data-testid="document-icon-indeed"
                                                    aria-hidden
                                                >
                                                    <path d="M13.149 1.8a2.247 2.247 0 0 1 1.596.66l4.803 4.8a2.247 2.247 0 0 1 .658 1.598v11.074c0 .63-.221 1.166-.665 1.61-.443.443-.98.664-1.61.664H6.076c-.63 0-1.166-.221-1.61-.665a2.193 2.193 0 0 1-.665-1.61V4.076c0-.63.222-1.166.665-1.61.444-.443.98-.665 1.61-.665h7.074Zm.145 10.469c-.53.31-1.155.484-1.818.484-.14 0-.273-.006-.411-.018v4.745c0 .438.106.767.311.985.213.226.471.335.797.335.33 0 .604-.11.809-.327.206-.22.312-.55.312-.994v-5.21Zm1.918-5.628c-1.18-.992-2.754-1.018-4.135-.534-2.004.812-3.398 2.584-4.028 4.712-.107.4-.193.8-.24 1.212 0 .02-.033.418.027.309.06-.096.086-.238.12-.348.304-1 .677-1.908 1.247-2.771C9.57 7.268 11.768 6.01 14.09 6.705c.431.142.816.348 1.201.593.074.051.611.465.491.09-.1-.295-.35-.548-.57-.747Zm-3.1 1.652a1.638 1.638 0 1 0 0 3.275 1.638 1.638 0 1 0 0-3.274Z" />
                                                </Icon>
                                            </Box>
                                        )}
                                        <Box
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                            }}
                                        >
                                            <Text variant="contrast" sx={{ fontWeight: '700' }}>
                                                {gt.gettext('Indeed Resume')}
                                            </Text>
                                            <Text level={2}>
                                                {gt.gettext('Updated')}{' '}
                                                {formatDate(indeedResume.dateModified)}
                                            </Text>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            display: 'none',
                                            [`input[id="resume-indeed-${indeedResume.id}"]:checked ~ * &`]:
                                                {
                                                    display: 'block',
                                                },
                                        }}
                                    >
                                        <CheckCircle aria-hidden />
                                    </Box>
                                </Box>

                                {/* Expanded Indeed Resume Details */}
                                <Box
                                    sx={{
                                        marginBlockStart: 3,
                                        paddingBlockStart: 3,
                                        borderBlockStart: '1px solid',
                                        borderBlockStartColor: 'border.weak',
                                        display: 'none',
                                        flexDirection: 'column',
                                        gap: 2,
                                        [`input[id="resume-indeed-${indeedResume.id}"]:checked ~ &`]:
                                            {
                                                display: 'flex',
                                            },
                                    }}
                                >
                                    {/* Name */}
                                    <Text sx={{ fontWeight: '700' }}>
                                        {indeedResume.firstName} {indeedResume.lastName}
                                    </Text>

                                    {/* Contact Details */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {indeedResume.phoneNumber && (
                                            <Text level={2}>{indeedResume.phoneNumber}</Text>
                                        )}
                                        {indeedResume.location?.formattedLocation && (
                                            <Text level={2}>
                                                {indeedResume.location.formattedLocation}
                                            </Text>
                                        )}
                                    </Box>

                                    {/* Work Experience Preview */}
                                    {indeedResume.workExperiences &&
                                        indeedResume.workExperiences.length > 0 && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 2,
                                                    marginBlockStart: 1,
                                                }}
                                            >
                                                {indeedResume.workExperiences
                                                    .slice(0, 2)
                                                    .map((experience, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 0.5,
                                                            }}
                                                        >
                                                            <Text level={2}>
                                                                {experience.title}
                                                            </Text>
                                                            <Text level={2}>
                                                                {experience.company}
                                                            </Text>
                                                        </Box>
                                                    ))}
                                            </Box>
                                        )}

                                    {/* Edit Button */}
                                    <ButtonLink
                                        variant="secondary"
                                        size="sm"
                                        full
                                        iconBefore={<Edit variant="stroke" aria-hidden />}
                                        href={indeedResumeEditLinkUrl}
                                        onClick={() =>
                                            eventLogger.logAutoApplySetupButtonClickEvent?.({
                                                currentPage: location.pathname,
                                                button: 'edit-indeed-resume',
                                            })
                                        }
                                    >
                                        {gt.gettext('Edit')}
                                    </ButtonLink>
                                </Box>
                            </Box>
                        )}

                        {/* Upload Resume Card - shown when no file resumes exist */}
                        {fileResumes.length === 0 && (
                            <Button
                                variant="secondary"
                                size="lg"
                                full
                                onClick={handleUploadClick}
                                disabled={uploading}
                                loading={uploading}
                                iconBefore={
                                    !sqPrefillEnabled ? (
                                        <Upload color="neutral.1300" aria-hidden />
                                    ) : undefined
                                }
                                sx={{ alignItems: 'flex-start' }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        flexGrow: 1,
                                    }}
                                >
                                    <Text sx={{ fontWeight: '700' }}>
                                        {gt.gettext('Upload a resume')}
                                    </Text>
                                    <Text sx={{ fontWeight: '400' }}>
                                        {gt.gettext(
                                            'Accepted file types are PDF, DOCX, RTF, or TXT',
                                        )}
                                    </Text>
                                </Box>
                            </Button>
                        )}

                        {/* File Resume Options */}
                        {fileResumes.map((fileResume) => (
                            <Box
                                key={fileResume.id}
                                as="label"
                                htmlFor={`resume-file-${fileResume.id}`}
                                data-dd-action-name="select file resume"
                                sx={{
                                    position: 'relative',
                                    borderRadius: 'md',
                                    padding: 4,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    gap: 2,
                                    flexDirection: 'column',
                                    transition: (theme) =>
                                        `border-color ${theme.transitions.default}`,
                                    border: '1px solid',
                                    borderColor: 'border.weak',
                                    '&:hover': {
                                        borderColor: 'border.primary',
                                    },
                                    '&:has(input:checked)': {
                                        border: '2px solid',
                                        borderColor: 'border.primary',
                                    },
                                    '&:has(input:focus-visible)': {
                                        outline: '2px solid',
                                        outlineOffset: '2px',
                                    },
                                }}
                            >
                                {/* Hidden Radio Input */}
                                <input
                                    type="radio"
                                    id={`resume-file-${fileResume.id}`}
                                    name="resume-selection"
                                    value={getRadioValue('file', fileResume.id)}
                                    checked={
                                        selectedRadioValue === getRadioValue('file', fileResume.id)
                                    }
                                    onChange={() => handleRadioChange('file', fileResume.id)}
                                    style={{
                                        position: 'absolute',
                                        clip: 'rect(0, 0, 0, 0)',
                                        clipPath: 'inset(50%)',
                                        height: '1px',
                                        width: '1px',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                    }}
                                />

                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            gap: 2,
                                            alignItems: 'flex-start',
                                            flex: 1,
                                        }}
                                    >
                                        {!sqPrefillEnabled && (
                                            <Box sx={{ flexShrink: 0 }}>
                                                <FileDoc aria-hidden />
                                            </Box>
                                        )}
                                        <Box
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                            }}
                                        >
                                            <Text variant="contrast" sx={{ fontWeight: '700' }}>
                                                {fileResume.fileName}
                                            </Text>
                                            <Text level={2}>
                                                {gt.gettext('Updated')}{' '}
                                                {formatDate(Number(fileResume.uploadTimestamp))}
                                            </Text>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            display: 'none',
                                            [`input[id="resume-file-${fileResume.id}"]:checked ~ * &`]:
                                                {
                                                    display: 'block',
                                                },
                                        }}
                                    >
                                        <CheckCircle aria-hidden />
                                    </Box>
                                </Box>

                                {/* Expanded File Resume Details */}
                                <Box
                                    sx={{
                                        marginBlockStart: 2,
                                        paddingBlockStart: 3,
                                        display: 'none',
                                        flexDirection: 'column',
                                        gap: 3,
                                        alignItems: 'center',
                                        [`input[id="resume-file-${fileResume.id}"]:checked ~ &`]: {
                                            display: 'flex',
                                        },
                                    }}
                                >
                                    {/* PDF Preview */}
                                    <FileResumePreview
                                        fileUrl={pdfUrlCache[fileResume.id] ?? null}
                                        fileName={fileResume.fileName ?? undefined}
                                        loading={!(fileResume.id in pdfUrlCache)}
                                        showBorder={true}
                                    />

                                    {/* Upload new file button */}
                                    <Button
                                        variant="secondary"
                                        full
                                        iconBefore={<Upload aria-hidden />}
                                        onClick={handleUploadClick}
                                        disabled={uploading}
                                        loading={uploading}
                                    >
                                        {gt.gettext('Upload file')}
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Application questions section - shown when SQ prefill is enabled,
                    hidden under progressive automation (IAFE-2081) */}
                    {showSqPrefillSection && (
                        <Flex sx={{ alignItems: 'flex-start', gap: 2 }}>
                            <Box sx={{ flexShrink: 0 }}>
                                <FileDoc aria-hidden />
                            </Box>
                            <Box>
                                <Heading level={3} as="h3" sx={{ marginBlockEnd: 0 }}>
                                    {gt.gettext('Use AI to answer application questions')}
                                </Heading>
                                <Flex
                                    sx={{
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                    }}
                                >
                                    <Box>
                                        <Text
                                            level={2}
                                            sx={{
                                                display: 'inline',
                                            }}
                                        >
                                            {copy.aiPrefillResumeIntro}
                                        </Text>{' '}
                                        <TextLink
                                            href="/auto-apply-setup/past-answers?from=auto-apply-setup"
                                            target="_blank"
                                            rel="noopener"
                                            iconAfter={<NewWindow />}
                                            aria-label={gt.gettext(
                                                'View past answers (opens in a new window)',
                                            )}
                                            sx={{ fontSize: 2 }}
                                            onClick={() => {
                                                eventLogger.logAutoApplySetupButtonClickEvent?.({
                                                    currentPage: location.pathname,
                                                    button: 'view-past-answers',
                                                });
                                            }}
                                        >
                                            {gt.gettext('View past answers')}
                                        </TextLink>
                                    </Box>
                                    <Switch
                                        as="span"
                                        id={switchId}
                                        aria-label={gt.gettext(
                                            'Use AI to answer application questions',
                                        )}
                                        checked={usePastAnswers}
                                        sx={{ padding: 0 }}
                                        onChange={(e) =>
                                            void handleUsePastAnswersChange(e.target.checked)
                                        }
                                    />
                                </Flex>
                            </Box>
                        </Flex>
                    )}
                    {/* Non-progressive keeps the primary action in-flow; the
                    progressive-automation bucket floats it via StickyActionFooter below. */}
                    {!isProgressiveAutomation && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4,
                                marginBlock: 6,
                            }}
                        >
                            {continueButton}
                        </Box>
                    )}
                </Box>

                {errorDialog}
            </PageTransition>
            {/* Progressive automation floats the primary CTA; other buckets keep the
                in-flow button above. */}
            {isProgressiveAutomation && (
                <StickyActionFooter maxInlineSize="25.125rem">{continueButton}</StickyActionFooter>
            )}
        </>
    );
};

export default ResumePage;
